import { getPurchaseJourneyContent } from '@/content/purchase-journey-content';
import { NullableAddressLookup } from '@/types';

import { unformattedTelPhoneNumber } from './constants';

const LANDLINE_PREFIXES = ['03', '04', '06', '07', '09'] as const;
const MOBILE_PREFIXES = ['020', '021', '022', '023', '024', '025', '027', '028', '029'] as const;

const DEFAULT_SHARED_VALIDATION = {
  phone: {
    required: 'Phone number is required',
    minDigits: 'Phone number must have minimum 6 digits.',
    prefix: 'Phone number must start with mobile or landline prefix',
    landlineLength: 'Landline must be 9 digits including area code.',
    mobileLength: 'Mobile number must be between 9 and 11 digits.',
    mobilePrefix: 'Phone number must start with 02.',
    mobileOnly: 'Phone number must be a mobile number',
    allZeros: 'Phone number cannot be all zeros',
    landlineOnly: 'Phone number must be a landline number',
    broadbandMobileRequired: 'Mobile number is required for Broadband connections',
  },
  address: {
    required: '{{addressType}} Address is required',
    fibreRequired: '{{addressType}} Address is required to select a fibre broadband plan.',
    minimumCharacters: 'Please enter at least 3 characters',
    selectFromDropdown: 'Please select an address from the suggestions or call {{phoneNumber}}',
  },
  errors: {
    generic: 'Something went wrong',
  },
};

const getSharedValidation = () => {
  try {
    return getPurchaseJourneyContent().sharedValidation;
  } catch {
    return DEFAULT_SHARED_VALIDATION;
  }
};

const interpolateValidationMessage = (message: string, values: Record<string, string | number>) =>
  Object.entries(values).reduce(
    (result, [key, value]) => result.replace(new RegExp(`{{${key}}}`, 'g'), `${value}`),
    message
  );

type PhoneNumberKind = 'landline' | 'mobile';

type SplitPhoneNumberResult = {
  digits: string;
  kind: PhoneNumberKind | null;
  areaCode: string;
  localNumber: string;
};

const validateAddress = (addressType: string | '') => {
  return (value: NullableAddressLookup) => {
    const addressValidation = getSharedValidation().address;
    let errs: string[] = [];
    const addressTypeLabel = addressType || '';
    if (!value)
      errs.push(
        interpolateValidationMessage(addressValidation.required, {
          addressType: addressTypeLabel,
        })
      );
    if (value) {
      if (value.label.length === 0)
        errs.push(
          interpolateValidationMessage(addressValidation.fibreRequired, {
            addressType: addressTypeLabel,
          })
        );
      if (value.label.length < 3) errs.push(addressValidation.minimumCharacters);
      if (value.id.length === 0)
        errs.push(
          interpolateValidationMessage(addressValidation.selectFromDropdown, {
            phoneNumber: unformattedTelPhoneNumber,
          })
        );
    }
    return errs;
  };
};

const checkDigitOnly = (str: string): boolean => /^\d+(-\d+)*$/.test(str);
/* eslint-disable no-useless-escape */
const checkValidCharacters = (str: string): boolean => /^[a-zA-Z\.\-\'‘\s]+$/.test(str);
const checkNameTiles = (str: string) =>
  str
    .split(' ')
    .some(s => ['mr', 'mrs', 'miss', 'ms', 'mx', 'sir', 'dr'].includes(s.toLowerCase()));

const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const normalizePhoneNumber = (value: string) => value.replace(/\D/g, '');

const splitPhoneNumber = (value: string): SplitPhoneNumberResult => {
  const digits = normalizePhoneNumber(value);

  if (!digits) {
    return {
      digits,
      kind: null,
      areaCode: '',
      localNumber: '',
    };
  }

  const landlinePrefix = LANDLINE_PREFIXES.find(prefix => digits.startsWith(prefix));
  if (landlinePrefix) {
    return {
      digits,
      kind: 'landline',
      areaCode: landlinePrefix,
      localNumber: digits.slice(landlinePrefix.length),
    };
  }

  const mobilePrefix = MOBILE_PREFIXES.find(prefix => digits.startsWith(prefix));
  if (mobilePrefix) {
    return {
      digits,
      kind: 'mobile',
      areaCode: mobilePrefix,
      localNumber: digits.slice(mobilePrefix.length),
    };
  }

  return {
    digits,
    kind: null,
    areaCode: '',
    localNumber: '',
  };
};

const composePhoneNumber = (areaCode: string, localNumber: string) => `${areaCode}${localNumber}`;

const validateUnparsedPhoneNumber = (digits: string, mobileOnly?: boolean): true | string => {
  const phoneValidation = getSharedValidation().phone;

  if (digits === '02') {
    return phoneValidation.mobileLength;
  }

  if (digits.startsWith('02')) {
    return mobileOnly ? phoneValidation.mobilePrefix : phoneValidation.prefix;
  }

  if (digits.length < 6) {
    return phoneValidation.minDigits;
  }

  return mobileOnly ? phoneValidation.mobilePrefix : phoneValidation.prefix;
};

const validateMobilePhoneNumber = (
  parsed: SplitPhoneNumberResult,
  mobileOnly?: boolean
): true | string => {
  const phoneValidation = getSharedValidation().phone;

  if (mobileOnly && parsed.kind !== 'mobile') {
    return phoneValidation.mobileOnly;
  }

  if (parsed.digits.length < 9 || parsed.digits.length > 11) {
    return phoneValidation.mobileLength;
  }

  if (/^0+$/.test(parsed.localNumber)) {
    return phoneValidation.allZeros;
  }

  return true;
};

const validateLandlinePhoneNumber = (parsed: SplitPhoneNumberResult): true | string => {
  const phoneValidation = getSharedValidation().phone;

  if (parsed.digits.length !== 9 || parsed.localNumber.length !== 7) {
    return phoneValidation.landlineLength;
  }

  if (/^0+$/.test(parsed.localNumber)) {
    return phoneValidation.allZeros;
  }

  return true;
};

const validatePhoneNumber = (
  value: string,
  options?: {
    mobileOnly?: boolean;
  }
): true | string => {
  const phoneValidation = getSharedValidation().phone;
  const digits = normalizePhoneNumber(value);

  if (!digits) {
    return phoneValidation.required;
  }

  if (digits.startsWith('64')) {
    return phoneValidation.prefix;
  }

  const parsed = splitPhoneNumber(digits);

  if (!parsed.kind) {
    return validateUnparsedPhoneNumber(digits, options?.mobileOnly);
  }

  if (options?.mobileOnly) {
    return validateMobilePhoneNumber(parsed, true);
  }

  if (parsed.kind === 'landline') {
    return validateLandlinePhoneNumber(parsed);
  }

  return validateMobilePhoneNumber(parsed);
};

const validateBroadbandMobileNumber = (value: string): true | string => {
  const phoneValidation = getSharedValidation().phone;
  const digits = normalizePhoneNumber(value);

  if (!digits) {
    return phoneValidation.required;
  }

  if (splitPhoneNumber(digits).kind === 'landline') {
    return (
      phoneValidation.broadbandMobileRequired ??
      DEFAULT_SHARED_VALIDATION.phone.broadbandMobileRequired
    );
  }

  return validatePhoneNumber(value, { mobileOnly: true });
};

const validateHomePhoneNumber = (value: string): true | string => {
  const phoneValidation = getSharedValidation().phone;
  const digits = normalizePhoneNumber(value);

  if (!digits) {
    return phoneValidation.required;
  }

  const parsed = splitPhoneNumber(digits);

  if (parsed.kind !== 'landline') {
    return phoneValidation.landlineOnly;
  }

  return validateLandlinePhoneNumber(parsed);
};

export {
  checkDigitOnly,
  checkNameTiles,
  checkValidCharacters,
  composePhoneNumber,
  normalizePhoneNumber,
  splitPhoneNumber,
  validateAddress,
  validateBroadbandMobileNumber,
  validateHomePhoneNumber,
  validatePhoneNumber,
  isValidEmail,
  LANDLINE_PREFIXES,
  MOBILE_PREFIXES,
};
